/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
//@ts-nocheck
import {
  IAgentPlugin,
  IPluginMethodMap,
} from '@vlinder-web/plugin-agent-service-react';
import config from '@pages/NomineeKYCPage/config/featuresSection.config.json';
import { vappContainer } from '@/application';

import { GET_MY_BANK_DETAIL } from '@pages/NomineeKYCPage/apis/graphql';

import { toHeaderCase } from 'js-convert-case';
import { StorageModule } from '@vlinder-web/storage-module-react';
import { AppNavigatorService } from '@/AppNavigator/services';

import moment from 'moment';
import jwt_decode from 'jwt-decode';

import { Graphql } from '@common/graphql';
import { apiConfig } from '@/config/apiConfig';

const loginSrv = () => {
  return vappContainer.get<AppNavigatorService>(
    AppNavigatorService.displayName
  );
};

export interface INomineeDetailMethods extends IPluginMethodMap {
  getNomineeDetailProps(args: any): Promise<any>;
}

export class NomineeDetailPlugin implements IAgentPlugin {
  readonly methods: INomineeDetailMethods;

  constructor() {
    this.methods = {
      getNomineeDetailProps: this.getProps.bind(this),
    };
  }

  async getProps(args: any) {
    return await this.getNomineeDetail(args);
  }

  private async getNomineeDetail(args: any) {
    try {
      let gql = Graphql.withBaseUrl(apiConfig.gql.url);
      // const token = getToken();
      const storageSrv = StorageModule.getInstance();
      let vuid = jwt_decode(loginSrv()?.getToken());
      const token = loginSrv()?.getToken();
      if (token) {
        gql = gql.withHeaders({
          authentication: token,
        });
      }
      const client = gql.getClient();
      const res = await client
        .query(GET_MY_BANK_DETAIL, {
          input: {
            vuid: vuid?.vuid,
          },
          input1: {
            where: [
              {
                column: 'performedBy',
                op: 'Equal',
                value: [vuid?.vuid],
              },
              {
                column: 'type',
                op: 'In',
                value: ['BANK_DETAIL'],
              },
            ],
          },
        })
        .toPromise();
      if (res?.data?.getBankAccountDetail) {
        console.log(`bank details from query`, res?.data?.getBankAccountDetail);
        let _res = res?.data?.getBankAccountDetail;
        let _txtResult = res?.data?.getTxnList?.data;
        return {
          title: config?.title,
          listItems: this.getListItems(_res),
          buttons: config?.buttons,
          txList: res?.data?.getTxnList,
          timelineData: this.getTimeLine(_txtResult),
          carouselItems: this.getCarouselItems(_res?.chequeLeaf),
          initialData: {
            accHolderName: _res?.accHolderName,
            bankName: _res?.bankName,
            accountNo: _res?.accountNo,
            type: _res?.type,
            swiftCode: _res?.swiftCode,
            chequeLeaf: [
              { base64: _res?.chequeLeaf?.doc, ..._res?.chequeLeaf },
            ],
          },
        };
      }
    } catch (err: any) {
      // log.error('error in capsules=', err?.message);
      throw Error(err?.response?.data?.error?.display_message);
    }
  }

  private getCarouselItems(item: any) {
    let _temp = [];
    _temp.push({
      ...item,
      type: this.getDocType(item?.fileType),
      src: item?.doc,
      title: item?.name,
      poster: item?.poster,
    });
    return _temp;
  }

  private getListItems(res: any) {
    let _temp = [];
    _temp = config?.listItems?.map((item) => {
      if (item?.title === 'Bank Name') {
        return {
          ...item,
          subTitle: `${toHeaderCase(res?.bankName ?? '')}`,
        };
      }
      if (item?.title === "Account Holder's Name") {
        return {
          ...item,
          subTitle: res?.accHolderName ?? '-',
        };
      }
      if (item?.title === 'Account Number') {
        return {
          ...item,
          subTitle: res?.accountNo ?? '-',
          masked: true,
        };
      }
      if (item?.title === 'Account Type') {
        return {
          ...item,
          subTitle: res?.type ?? '-',
        };
      }
      if (item?.title === 'IFSC Code') {
        return {
          ...item,
          subTitle: res?.swiftCode,
        };
      }
    });
    return _temp;
  }

  private getDocType(res: any) {
    switch (res) {
      case 'application/pdf':
        return 'pdf';
      case 'image/png':
        return 'img';
      case 'image/jpg':
        return 'img';
      case 'image/jpeg':
        return 'img';
      default:
        return null;
    }
  }

  private getTimeLine(data: any) {
    let _temp: any = [];
    Array.isArray(data) &&
      data?.forEach((element) => {
        _temp.push({
          key: element?.id,
          headline: toHeaderCase(element?.action ?? ''),
          headlineBackgroundColor: '#579783',
          children: this.getChildren(element),
        });
      });

    return _temp;
  }

  private getChildren(data: any) {
    let _temp: any = [];
    _temp.push({
      itemId: data?.id,
      multiColList: this.getProvenanceAccordion(data),
      title: toHeaderCase(data?.action ?? ''),
      subtitle: data?.payload?.comment,
    });

    return _temp;
  }

  private getProvenanceAccordion(res: any) {
    let _temp: any = [];
    if (res) {
      let _list: any = [];
      let _date: any = [];
      let _location: any = [];
      if (res?.performedBy?.firstName) {
        _list.push({
          title: toHeaderCase('by'),
          subTitle: `${toHeaderCase(
            res?.performedBy?.firstName
          )} ${toHeaderCase(res?.performedBy?.lastName)}`,
          icon: config?.orgIcon,
        });
      }
      if (res?.location) {
        _location.push({
          title: toHeaderCase('at'),
          subTitle: res?.location ?? '-',
          icon: config?.locationIcon,
        });
      }
      if (res?.when) {
        _date.push({
          title: toHeaderCase('on'),
          subTitle: res?.when ? moment(parseInt(res?.when)).format('LLL') : '-',
          icon: config?.dateIcon,
        });
      }
      _temp = [..._list, ..._location, ..._date];
    }
    return {
      numberOfColumns: '2',
      data: _temp,
    };
  }
}
